import { useEffect, useState } from 'react';
import {
  getDmExceptions,
  DmStagingApiError,
  type DmExceptionsResponse,
  type DmExceptionItem,
  type DmExceptionKind,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalPageHeader,
  CanonicalFilterBar,
  CanonicalMetricGrid,
  CanonicalMetricCard,
  DataTable,
  DataTableFooter,
  EmptyState,
  Pagination,
  PageSizeSelector,
  ResultsCounter,
  SearchField,
  SelectField,
  StatePanel,
  StatusBadge,
  canonicalStates,
  type DataColumn,
} from '@urbanik/ui';
import { apiBase } from './api-base';
import { DmExceptionResolutionModal } from './DmExceptionResolutionModal';
const labels: Record<DmExceptionKind, string> = {
  titleholder_mismatch: 'Pago sin titular histórico confirmado',
  non_monetary: 'No monetario',
  missing_member: 'Socio sin correspondencia',
  duplicate: 'Posible duplicado',
};
export function DmExceptionsView() {
  const [data, setData] = useState<DmExceptionsResponse | null>(null),
    [state, setState] = useState<'loading' | 'success' | 'error' | 'forbidden'>('loading');
  const [query, setQuery] = useState(''),
    [search, setSearch] = useState(''),
    [year, setYear] = useState(''),
    [month, setMonth] = useState(''),
    [kind, setKind] = useState(''),
    [page, setPage] = useState(1),
    [pageSize, setPageSize] = useState(25),
    [attempt, setAttempt] = useState(0);
  const [selectedException, setSelectedException] = useState<DmExceptionItem | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query);
      setPage(1);
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    getDmExceptions(
      apiBase,
      {
        page,
        pageSize,
        query: search,
        ...(year ? { year: Number(year) } : {}),
        ...(month ? { month: Number(month) } : {}),
        ...(kind ? { kind: kind as DmExceptionKind } : {}),
      },
      controller.signal,
    )
      .then((result) => {
        if (!controller.signal.aborted) {
          setData(result);
          setState('success');
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        if (error instanceof DmStagingApiError && error.kind === 'unauthorized') {
          location.hash = '#login';
          return;
        }
        setState(
          error instanceof DmStagingApiError && error.kind === 'forbidden' ? 'forbidden' : 'error',
        );
      });
    return () => controller.abort();
  }, [page, pageSize, search, year, month, kind, attempt]);
  const columns: readonly DataColumn<DmExceptionItem>[] = [
    {
      id: 'period',
      header: 'Período',
      accessor: (r) => `${r.year}-${String(r.month).padStart(2, '0')}`,
      sortable: false,
    },
    {
      id: 'member',
      header: 'Socio / referencia',
      accessor: (r) => r.memberLabel,
      sortable: false,
      cell: (r) => (
        <div>
          <strong>{r.memberLabel || 'Sin nombre'}</strong>
          <p>Referencia {r.memberReference || '—'}</p>
        </div>
      ),
    },
    {
      id: 'reason',
      header: 'Excepciones',
      accessor: (r) => r.reasons.join(', '),
      sortable: false,
      cell: (r) => (
        <div>
          {r.reasons.map((reason) => (
            <p key={reason}>
              <StatusBadge tone="warning">{labels[reason]}</StatusBadge>
            </p>
          ))}
        </div>
      ),
    },
    {
      id: 'raw',
      header: 'Valor original',
      accessor: (r) => r.rawValue ?? 'Vacío',
      sortable: false,
    },
    {
      id: 'origin',
      header: 'Evidencia de origen',
      accessor: (r) => `${r.sourceSheet}!${r.sourceCell}`,
      sortable: false,
      cell: (r) => (
        <details>
          <summary>
            {r.sourceSheet}!{r.sourceCell}
          </summary>
          <p>{r.explanation}</p>
          <p>Observación: {r.sourceObservation || 'Sin comentario'}</p>
          {r.relatedOrigins.length > 0 ? (
            <p>Celdas relacionadas: {r.relatedOrigins.join(', ')}</p>
          ) : null}
        </details>
      ),
    },
    {
      id: 'action',
      header: 'Acción',
      accessor: () => '',
      sortable: false,
      cell: (r) =>
        r.paymentStatus === 'exception' ? (
          <Button variant="secondary" onClick={() => setSelectedException(r)}>
            Resolver
          </Button>
        ) : null,
    },
  ];
  return (
    <>
      <section className="dm-staging-view">
        <CanonicalPageHeader
          title="Excepciones DM"
          description="M07.2 · Revisión del staging histórico. No crea ni modifica obligaciones, pagos o comprobantes."
          actions={
            <Button onClick={() => setAttempt((v) => v + 1)} disabled={state === 'loading'}>
              Actualizar revisión
            </Button>
          }
        />
        <CanonicalFilterBar>
          <SearchField
            label="Buscar socio, referencia, celda u observación"
            value={query}
            onChange={setQuery}
          />
          <SelectField
            label="Tipo de excepción"
            value={kind}
            onValueChange={(v) => {
              setKind(v);
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todas las excepciones' },
              ...Object.entries(labels).map(([value, label]) => ({ value, label })),
            ]}
          />
          <SelectField
            label="Año"
            value={year}
            onValueChange={(v) => {
              setYear(v);
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos los años' },
              ...(data?.years ?? []).map((y) => ({ value: String(y), label: String(y) })),
            ]}
          />
          <SelectField
            label="Mes"
            value={month}
            onValueChange={(v) => {
              setMonth(v);
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos los meses' },
              ...[
                'Enero',
                'Febrero',
                'Marzo',
                'Abril',
                'Mayo',
                'Junio',
                'Julio',
                'Agosto',
                'Septiembre',
                'Octubre',
                'Noviembre',
                'Diciembre',
              ].map((label, i) => ({ value: String(i + 1), label })),
            ]}
          />
        </CanonicalFilterBar>
        {state !== 'success' ? (
          <StatePanel
            state={canonicalStates.find((s) => s.kind === state)!}
            onAction={() => setAttempt((v) => v + 1)}
          />
        ) : data ? (
          <>
            <CanonicalMetricGrid>
              <CanonicalMetricCard label="Celdas evaluadas" value={data.evaluatedRows} />
              <CanonicalMetricCard label="No monetarios" value={data.counts.non_monetary} />
              <CanonicalMetricCard label="Sin correspondencia" value={data.counts.missing_member} />
              <CanonicalMetricCard label="Posibles duplicados" value={data.counts.duplicate} />
              <CanonicalMetricCard
                label="Pagos sin titular confirmado"
                value={data.counts.titleholder_mismatch}
              />
            </CanonicalMetricGrid>
            <p role="status">
              {data.exceptionRows} celdas con excepciones en el lote. Una celda puede tener varios
              motivos; los indicadores corresponden al lote completo.
            </p>
            {data.batch ? (
              <p>
                Fuente: {data.batch.sourceFile} · Lote: {data.batch.id}
              </p>
            ) : null}
            {data.items.length === 0 ? (
              <EmptyState
                title={
                  !data.batch
                    ? 'Sin staging DM'
                    : data.totalItems === 0
                      ? 'Sin excepciones para estos filtros'
                      : 'Página sin resultados'
                }
                description={
                  !data.batch
                    ? 'Transforma la fuente en Explorador DM antes de revisar.'
                    : 'Ajusta los filtros o vuelve a la primera página. Esto no confirma ni importa datos financieros.'
                }
              />
            ) : (
              <DataTable
                paginate={false}
                rows={data.items}
                columns={columns}
                visibleColumnIds={new Set(columns.map((c) => c.id))}
                sort={{ columnId: 'period', direction: 'descending' }}
                onSort={() => {}}
                empty={
                  <EmptyState
                    title="Sin excepciones"
                    description="No hay filas para esta consulta."
                  />
                }
              />
            )}
            <DataTableFooter>
              <ResultsCounter
                from={data.items.length ? (data.page - 1) * data.pageSize + 1 : 0}
                to={data.items.length ? (data.page - 1) * data.pageSize + data.items.length : 0}
                total={data.totalItems}
                noun="celdas"
              />
              <Pagination page={page} pages={Math.max(1, data.totalPages)} onChange={setPage} />
              <PageSizeSelector
                value={pageSize}
                options={[10, 25, 50, 100]}
                onChange={(v) => {
                  setPageSize(v);
                  setPage(1);
                }}
              />
            </DataTableFooter>
          </>
        ) : null}
      </section>
      <DmExceptionResolutionModal
        item={selectedException}
        onClose={() => setSelectedException(null)}
        onResolved={() => {
          setAttempt((value) => value + 1);
        }}
      />
    </>
  );
}
